import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  GraduationCap, Award, Sparkles, Flame, Clock, BookOpen, Target, ArrowUpRight,
  Lock, Search, FileDown, StickyNote, Rocket, Building2, Landmark, CreditCard,
  Banknote, Megaphone, Settings2, TrendingUp, Palette, Handshake, Heart, Users,
  Calculator, ShieldCheck, Cpu, Crown, Zap, Expand,
} from "lucide-react";
import api from "@/lib/api";
import { API } from "@/lib/api";
import { PageHeader } from "@/pages/Dashboard";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { AcademyPath, AcademyCourse } from "@/pages/academy/AcademyCourse";
import { AcademyLesson } from "@/pages/academy/AcademyLesson";
import { BusinessMasteryCard } from "@/components/BusinessMasteryCard";

const ICONS = {
  Rocket, Building2, Landmark, CreditCard, Banknote, Megaphone, Settings2, TrendingUp,
  Palette, Handshake, Heart, Users, Calculator, ShieldCheck, Cpu, Crown, Zap, Expand, BookOpen,
};
const TIER_LABEL = { free: "Free", starter: "Starter", growth: "Growth", enterprise: "Enterprise" };
const STAGES = ["idea", "formation", "banking", "credit", "funding", "growth", "scale"];

function StatTile({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl border border-white/8 bg-[#121214] p-4">
      <Icon className="w-5 h-5 text-[#D4AF37] mb-2" />
      <div className="text-2xl font-semibold tabular-nums text-white">{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-[#71717A]">{label}</div>
    </div>
  );
}

function DashboardView({ data, onOpenPath, onOpenCourse, onOpenLesson, onOpenNotes }) {
  const navigate = useNavigate();
  const s = data.stats;
  return (
    <div className="fv-fade-up">
      <div className="flex items-center justify-between mb-5">
        <div className="text-sm text-[#A1A1AA]">Your stage: <span className="text-[#D4AF37] font-medium capitalize" data-testid="lms-stage">{data.stage}</span> · {TIER_LABEL[data.tier]} plan</div>
        <button onClick={onOpenNotes} data-testid="lms-notes-btn" className="flex items-center gap-1.5 text-sm text-[#A1A1AA] hover:text-white"><StickyNote className="w-4 h-4" /> My notes</button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="lms-stats">
        <StatTile icon={Award} label="Courses done" value={s.courses_completed} />
        <StatTile icon={BookOpen} label="In progress" value={s.courses_in_progress} />
        <StatTile icon={Flame} label="Day streak" value={s.streak_days} />
        <StatTile icon={Clock} label="Learning hours" value={s.total_hours} />
      </div>

      {data.next_lesson && data.current_course && (
        <button onClick={() => onOpenLesson(data.next_lesson.lesson_id, data.current_course.slug)} data-testid="lms-continue"
          className="w-full text-left rounded-2xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#151310] to-[#121214] p-5 mb-6 hover:border-[#D4AF37]/50 transition-colors">
          <div className="text-[11px] uppercase tracking-wider text-[#D4AF37] mb-1">Continue learning</div>
          <div className="font-heading text-lg text-white">{data.next_lesson.title}</div>
          <div className="text-sm text-[#A1A1AA]">{data.current_course.title} · {data.current_course.progress}% complete</div>
        </button>
      )}

      {data.recommendations?.length > 0 && (
        <div className="rounded-2xl border border-white/8 bg-[#121214] p-6 mb-6" data-testid="lms-recommendations">
          <div className="flex items-center gap-2 mb-3"><Sparkles className="w-5 h-5 text-[#D4AF37]" /><h3 className="font-heading text-lg text-white">Recommended for your stage</h3></div>
          <div className="grid sm:grid-cols-3 gap-3 mb-4">
            {data.recommendations.map((r) => (
              <button key={r.course_id} onClick={() => !r.locked && onOpenCourse(r.slug)} disabled={r.locked} data-testid={`lms-rec-${r.slug}`}
                className={`text-left rounded-xl border p-4 transition-colors ${r.locked ? "border-white/5 bg-[#0d0d0f]" : "border-[#D4AF37]/20 bg-[#151310] hover:border-[#D4AF37]/40"}`}>
                <div className="flex items-start gap-2">{r.locked ? <Lock className="w-4 h-4 text-[#71717A] mt-0.5 shrink-0" /> : <Target className="w-4 h-4 text-[#D4AF37] mt-0.5 shrink-0" />}
                  <div><div className="text-white text-sm font-medium">{r.title}</div><div className="text-xs text-[#71717A] mt-0.5">{r.locked ? `Requires ${TIER_LABEL[r.tier]} plan` : r.reason}</div></div>
                </div>
              </button>
            ))}
          </div>
          <button onClick={() => navigate("/ai-assistant")} data-testid="lms-ai-cta" className="text-sm text-[#D4AF37] hover:underline flex items-center gap-1">Ask the AI Advisor what to learn next <ArrowUpRight className="w-4 h-4" /></button>
        </div>
      )}
      <div className="mt-6" data-testid="bm-academy-featured">
        <BusinessMasteryCard variant="featured" source="academy_dashboard" location="academy_home" />
      </div>
    </div>
  );
}

function PathBrowser({ onOpenPath }) {
  const [d, setD] = useState(null);
  const [q, setQ] = useState("");
  const [stage, setStage] = useState("All");
  useEffect(() => { api.get("/lms/paths").then(({ data }) => setD(data)).catch(() => {}); }, []);
  if (!d) return <p className="text-[#71717A]">Loading paths…</p>;
  const shown = d.paths.filter((p) => (stage === "All" || p.stage === stage) && p.title.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="fv-fade-up">
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-[#52525B] absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="lms-path-search" placeholder="Search learning paths…"
            className="w-full bg-[#121214] border border-white/10 rounded-full pl-9 pr-4 h-10 text-sm text-white placeholder:text-[#52525B] focus:outline-none focus:border-[#D4AF37]/40" />
        </div>
        <div className="flex gap-1.5 flex-wrap" data-testid="lms-stage-filters">
          {["All", ...STAGES].map((st) => (
            <button key={st} onClick={() => setStage(st)} data-testid={`stage-filter-${st}`}
              className={`text-xs px-3 py-1.5 rounded-full capitalize transition-colors ${stage === st ? "fv-gold-gradient text-[#0A0A0B] font-medium" : "bg-white/5 text-[#A1A1AA] hover:text-white"}`}>{st}</button>
          ))}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="lms-paths-grid">
        {shown.map((p) => {
          const Icon = ICONS[p.icon] || BookOpen;
          return (
            <button key={p.path_id} onClick={() => onOpenPath(p.slug)} data-testid={`lms-path-${p.slug}`}
              className="fv-card-hover text-left rounded-2xl border border-white/8 bg-[#121214] p-5 flex flex-col">
              <div className="flex items-start justify-between mb-3">
                <div className="w-11 h-11 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center"><Icon className="w-5 h-5 text-[#D4AF37]" /></div>
                <div className="flex items-center gap-1.5">
                  {p.locked && <Lock className="w-3.5 h-3.5 text-[#71717A]" />}
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/8 text-[#A1A1AA] capitalize">{p.stage}</span>
                </div>
              </div>
              <h3 className="font-heading text-lg text-white mb-1">{p.title}</h3>
              <p className="text-sm text-[#71717A] leading-relaxed flex-1 mb-3 line-clamp-2">{p.description}</p>
              <div className="flex items-center justify-between mb-2 text-xs">
                <span className="text-[#A1A1AA]">{p.courses_count} course{p.courses_count !== 1 ? "s" : ""} · {p.lessons_total} lessons</span>
                <span className="text-[#D4AF37]">{p.progress}%</span>
              </div>
              <Progress value={p.progress} className="h-1.5 bg-white/5" />
            </button>
          );
        })}
      </div>
    </div>
  );
}

function NotesView({ onBack }) {
  const [notes, setNotes] = useState([]);
  const [q, setQ] = useState("");
  const load = useCallback((query) => api.get(`/lms/notes${query ? `?q=${encodeURIComponent(query)}` : ""}`).then(({ data }) => setNotes(data.notes)).catch(() => {}), []);
  useEffect(() => { load(""); }, [load]);
  useEffect(() => { const t = setTimeout(() => load(q), 300); return () => clearTimeout(t); }, [q, load]);

  return (
    <div className="fv-fade-up" data-testid="lms-notes-view">
      <button onClick={onBack} className="text-sm text-[#A1A1AA] hover:text-white mb-4">← Back to Academy</button>
      <div className="flex items-center justify-between mb-5">
        <h1 className="font-heading text-2xl text-white">My Notes</h1>
        <button onClick={() => { window.open(`${API}/lms/notes/download`, "_blank"); toast.success("Downloading your notes"); }} data-testid="notes-download-btn"
          className="flex items-center gap-1.5 text-sm text-[#D4AF37] border border-[#D4AF37]/25 rounded-full px-3 py-1.5 hover:bg-[#D4AF37]/10"><FileDown className="w-4 h-4" /> Download all</button>
      </div>
      <div className="relative mb-5">
        <Search className="w-4 h-4 text-[#52525B] absolute left-3 top-1/2 -translate-y-1/2" />
        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="notes-search" placeholder="Search your notes…"
          className="w-full bg-[#121214] border border-white/10 rounded-full pl-9 pr-4 h-10 text-sm text-white placeholder:text-[#52525B] focus:outline-none focus:border-[#D4AF37]/40" />
      </div>
      <div className="space-y-3" data-testid="notes-list">
        {notes.length === 0 && <p className="text-sm text-[#71717A]">No notes yet — take notes inside any lesson.</p>}
        {notes.map((n) => (
          <div key={n.lesson_id} className="rounded-xl border border-white/8 bg-[#121214] p-4">
            <div className="text-[10px] uppercase tracking-wider text-[#71717A] mb-1">{n.course_title}</div>
            <div className="text-sm text-white font-medium mb-1.5">{n.lesson_title}</div>
            <p className="text-sm text-[#A1A1AA] whitespace-pre-wrap">{n.content}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Education() {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [view, setView] = useState({ mode: "home" });
  const [refreshKey, setRefreshKey] = useState(0);

  const loadDashboard = useCallback(() => api.get("/lms/dashboard").then(({ data }) => setDashboard(data)).catch(() => {}), []);
  useEffect(() => { loadDashboard(); }, [loadDashboard, refreshKey]);

  const go = (v) => { window.scrollTo(0, 0); setView(v); };
  const openPath = (slug) => go({ mode: "path", slug });
  const openCourse = (slug) => go({ mode: "course", slug });
  const openLesson = (lessonId, courseSlug) => go({ mode: "lesson", lessonId, courseSlug });
  const backHome = () => { setRefreshKey((k) => k + 1); go({ mode: "home" }); };

  if (view.mode === "path") return <AcademyPath slug={view.slug} onBack={backHome} onOpenCourse={openCourse} />;
  if (view.mode === "course") return <AcademyCourse slug={view.slug} refreshKey={refreshKey} onBack={backHome} onOpenLesson={(lid) => openLesson(lid, view.slug)} onOpenWorkflow={(p) => navigate(p)} />;
  if (view.mode === "lesson") return <AcademyLesson lessonId={view.lessonId} onBack={() => openCourse(view.courseSlug)} onNavigate={(lid) => openLesson(lid, view.courseSlug)} onOpenWorkflow={(p) => navigate(p)} onChanged={() => setRefreshKey((k) => k + 1)} />;
  if (view.mode === "notes") return <NotesView onBack={backHome} />;

  if (!dashboard) return <p className="text-[#71717A]">Loading Academy…</p>;

  return (
    <div className="fv-fade-up">
      <PageHeader eyebrow="Grow" title="Fairview Academy"
        subtitle="Your personalized business learning platform — courses, quizzes, certificates, and an AI coach, all connected to your real business." />
      <DashboardView data={dashboard} onOpenPath={openPath} onOpenCourse={openCourse}
        onOpenLesson={openLesson} onOpenNotes={() => go({ mode: "notes" })} />
      <div className="flex items-center gap-2 mb-4 mt-2">
        <GraduationCap className="w-5 h-5 text-[#D4AF37]" />
        <h2 className="font-heading text-xl text-white">Learning paths</h2>
      </div>
      <PathBrowser onOpenPath={openPath} />
    </div>
  );
}
